---
title: Your first persona in 10 minutes
summary: Use the guided wizard to create an expert persona, preview it, and start chatting.
visibility: authenticated
feature: persona_wizard
audience: author-admin
section: Get started
order: 15
last_verified: 2026-07-21
owner: docs
---

# Your first persona in 10 minutes

This walkthrough takes you from an empty workspace to a working expert you can chat
with, using the guided wizard. You won't write any code.

## 1. Open the wizard

Go to **Personas** in the sidebar and click **New Persona**. This opens the guided
wizard. (If you'd rather skip the wizard, **Start blank** opens a small dialog — just a
name and optional description — and drops you straight into the **Workshop** to build the
persona yourself. The wizard is the fastest way to a first draft.)

## 2. Answer five questions

The wizard asks five short questions and turns your answers into a starting persona:

1. **What does your business do?** — a sentence or two of context.
2. **Who are your customers?** — who the persona will be talking to.
3. **What personality should it have?** — pick a preset (for example *Warm and
   friendly* or *Formal and precise*) or describe your own.
4. **Which languages should it speak?** — choose one or more.
5. **Pick a starter recipe** *(optional)* — a template such as *Bakery / catering* or
   *Customer support*, plus a name for your persona.

Click **Generate persona**.

## 3. Preview and refine

The wizard generates a draft and shows you a preview:

- The **Generated persona** panel summarizes what it built.
- The **Live preview** lets you type a sample message and **Simulate one turn** to see
  how the persona responds — before anyone else talks to it.

When you're happy, click **Publish**. Your persona is saved and you land in the
**Workshop**, on its **Review** tab — click **Review now** whenever you want an
LLM-based check of the prompt (it doesn't run automatically).

## 4. Start a conversation

From **Home**, find your persona and click **Start Conversation**. Type a question and
send it. The reply streams in, and when the persona draws on its knowledge base you'll
see **source citations** beside the answer — click one to read the passage it used.

## What's next

- Give the persona its own knowledge: [Connect a persona to its knowledge](/help/how-to/connect-knowledge-rag).
- Understand how grounded answers work: [How RAG works here](/help/concepts/how-rag-works).
- See every setting you can tune: [Persona configuration reference](/help/reference/persona-config).
